import type { MapAdapter } from './MapAdapter';
import type {
  GoogleDirectionsRenderer,
  GoogleDirectionsService,
  GoogleDistanceMatrixService,
  GoogleInfoWindowInstance,
  GoogleMapInstance,
  GoogleMarkerInstance,
  GoogleMapsNamespace,
  GooglePolylineInstance,
} from './scriptLoader';
import { loadGoogleMaps } from './scriptLoader';
import type { LatLng, MapInitOptions, MapMarker, MapMarkerRole, RouteDrawResult, RouteResult } from './types';
import { describeRoutePlanningError } from './tripMapPaths';
import {
  BUS_MARKER_ICON_URL,
  MARKER_COLORS,
  STOP_MARKER_ANCHOR,
  STOP_MARKER_SIZE,
  isBusMarkerRole,
  isStopMarkerRole,
  markerZIndex,
  stopMarkerIconUrl,
} from './markerIcons';

function markerPopupHtml(marker: MapMarker): string {
  const title = marker.title ?? '';
  if (marker.subtitle) {
    return `<div style="font-family:sans-serif;font-size:13px;line-height:1.4"><strong>${title}</strong><br/>${marker.subtitle}</div>`;
  }
  return `<div style="font-family:sans-serif;font-size:13px">${title}</div>`;
}

function circleMarkerIcon(maps: GoogleMapsNamespace, role?: MapMarkerRole) {
  const scale = role === 'track' ? 4 : 8;
  return {
    path: maps.SymbolPath.CIRCLE,
    scale,
    fillColor: MARKER_COLORS[role || 'driver'],
    fillOpacity: role === 'track' ? 0.85 : 1,
    strokeColor: '#FFFFFF',
    strokeWeight: role === 'track' ? 1 : 2,
  };
}

function markerIcon(maps: GoogleMapsNamespace, role?: MapMarkerRole) {
  if (isStopMarkerRole(role)) {
    return {
      url: stopMarkerIconUrl(role),
      // Google Maps requires Size/Point instances — plain objects often hide the pin.
      scaledSize: new maps.Size(STOP_MARKER_SIZE.width, STOP_MARKER_SIZE.height),
      anchor: new maps.Point(STOP_MARKER_ANCHOR.x, STOP_MARKER_ANCHOR.y),
    };
  }
  if (isBusMarkerRole(role)) {
    return {
      url: BUS_MARKER_ICON_URL,
      scaledSize: new maps.Size(40, 40),
      anchor: new maps.Point(20, 20),
    };
  }
  return circleMarkerIcon(maps, role);
}

export class GoogleMapAdapter implements MapAdapter {
  readonly provider = 'google' as const;

  private maps!: GoogleMapsNamespace;
  private map: GoogleMapInstance | null = null;
  private markers = new Map<string, GoogleMarkerInstance>();
  private directionsRenderer: GoogleDirectionsRenderer | null = null;
  private directionsService: GoogleDirectionsService | null = null;
  private distanceMatrixService: GoogleDistanceMatrixService | null = null;
  private trackPolyline: GooglePolylineInstance | null = null;
  private infoWindow: GoogleInfoWindowInstance | null = null;
  private markerMeta = new Map<string, MapMarker>();

  constructor(private readonly apiKey: string) {}

  async init(container: HTMLElement, options: MapInitOptions): Promise<void> {
    this.maps = await loadGoogleMaps(this.apiKey);
    this.map = new this.maps.Map(container, {
      center: options.center,
      zoom: options.zoom ?? 13,
      mapTypeId: 'roadmap',
      fullscreenControl: !options.compact,
      streetViewControl: false,
      mapTypeControl: !options.compact,
      zoomControl: true,
      scrollwheel: true,
      gestureHandling:
        options.gestureHandling ?? (options.compact ? 'cooperative' : 'greedy'),
    });
    this.directionsService = new this.maps.DirectionsService();
    this.directionsRenderer = new this.maps.DirectionsRenderer({
      suppressMarkers: true,
      polylineOptions: { strokeColor: '#2F80ED', strokeWeight: 5 },
    });
    this.directionsRenderer.setMap(this.map);
    this.distanceMatrixService = new this.maps.DistanceMatrixService();
    this.infoWindow = new this.maps.InfoWindow();
  }

  private bindMarkerClick(marker: GoogleMarkerInstance, item: MapMarker): void {
    marker.addListener?.('click', () => {
      if (!this.map || !this.infoWindow) return;
      this.infoWindow.setContent(markerPopupHtml(item));
      this.infoWindow.open(this.map, marker);
    });
  }

  private applyMarkerLabel(marker: GoogleMarkerInstance, item: MapMarker): void {
    if (item.label && item.role === 'track') {
      marker.setLabel?.({
        text: item.label,
        color: '#9A3412',
        fontSize: '9px',
      });
    }
  }

  setMarkers(next: MapMarker[]): void {
    if (!this.map) return;
    const ids = new Set(next.map((m) => m.id));
    for (const [id, marker] of this.markers) {
      if (!ids.has(id)) {
        marker.setMap(null);
        this.markers.delete(id);
        this.markerMeta.delete(id);
      }
    }
    for (const item of next) {
      this.markerMeta.set(item.id, item);
      const existing = this.markers.get(item.id);
      if (existing) {
        existing.setPosition(item.position);
        this.applyMarkerLabel(existing, item);
        continue;
      }
      const marker = new this.maps.Marker({
        map: this.map,
        position: item.position,
        title: item.subtitle ? `${item.title ?? ''} — ${item.subtitle}` : item.title,
        icon: markerIcon(this.maps, item.role),
        zIndex: markerZIndex(item.role),
      });
      this.applyMarkerLabel(marker, item);
      this.bindMarkerClick(marker, item);
      this.markers.set(item.id, marker);
    }
  }

  updateMarker(id: string, position: LatLng): void {
    const marker = this.markers.get(id);
    if (marker) marker.setPosition(position);
  }

  drawRoute(from: LatLng, to: LatLng): Promise<RouteDrawResult> {
    this.clearTrack();
    if (!this.map || !this.directionsService || !this.directionsRenderer) {
      return Promise.resolve({
        ok: false,
        error: describeRoutePlanningError(this.provider, 'MAP_NOT_READY'),
      });
    }
    this.directionsRenderer.setMap(this.map);
    return new Promise((resolve) => {
      this.directionsService!.route(
        {
          origin: from,
          destination: to,
          travelMode: this.maps.TravelMode.DRIVING,
        },
        (result, status) => {
          if (status === 'OK' && result) {
            this.directionsRenderer!.setDirections(result);
            resolve({ ok: true });
            return;
          }
          this.directionsRenderer!.setDirections(null);
          resolve({ ok: false, error: status || 'UNKNOWN' });
        }
      );
    });
  }

  drawTrack(points: LatLng[]): Promise<void> {
    this.clearRoute();
    if (!this.map || points.length < 2) return Promise.resolve();

    // Hide planned-route overlay while showing GPS history.
    this.directionsRenderer?.setMap(null);

    if (this.trackPolyline) {
      this.trackPolyline.setPath(points);
      this.trackPolyline.setMap(this.map);
    } else {
      this.trackPolyline = new this.maps.Polyline({
        path: points,
        geodesic: false,
        strokeColor: '#EA580C',
        strokeOpacity: 0.95,
        strokeWeight: 5,
        map: this.map,
      });
    }
    return Promise.resolve();
  }

  clearRoute(): void {
    this.directionsRenderer?.setDirections(null);
    this.directionsRenderer?.setMap(null);
  }

  clearTrack(): void {
    this.trackPolyline?.setMap(null);
    this.trackPolyline = null;
  }

  panTo(position: LatLng): void {
    this.map?.panTo(position);
  }

  setZoom(zoom: number): void {
    this.map?.setZoom?.(zoom);
  }

  fitPoints(points: LatLng[]): void {
    if (!this.map || points.length === 0) return;
    const bounds = new this.maps.LatLngBounds();
    points.forEach((p) => bounds.extend(p));
    this.map.fitBounds(bounds);
  }

  getDrivingRoute(from: LatLng, to: LatLng | string): Promise<RouteResult | null> {
    if (!this.distanceMatrixService) return Promise.resolve(null);
    return new Promise((resolve) => {
      this.distanceMatrixService!.getDistanceMatrix(
        {
          origins: [from],
          destinations: [to],
          travelMode: this.maps.TravelMode.DRIVING,
          drivingOptions: {
            departureTime: new Date(),
            trafficModel: this.maps.TrafficModel.BEST_GUESS,
          },
          unitSystem: this.maps.UnitSystem.METRIC,
        },
        (response, status) => {
          const element = response?.rows?.[0]?.elements?.[0];
          if (status !== 'OK' || element?.status !== 'OK') {
            resolve(null);
            return;
          }
          resolve({
            distanceMeters: element.distance?.value ?? 0,
            durationSeconds: element.duration_in_traffic?.value ?? element.duration?.value ?? 0,
          });
        }
      );
    });
  }

  reverseGeocode(position: LatLng): Promise<string> {
    return new Promise((resolve) => {
      const geocoder = new this.maps.Geocoder();
      geocoder.geocode({ location: position }, (results, status) => {
        if (status === 'OK' && results?.[0]) {
          resolve(results[0].formatted_address);
        } else {
          resolve(`${position.lat.toFixed(5)}, ${position.lng.toFixed(5)}`);
        }
      });
    });
  }

  destroy(): void {
    for (const marker of this.markers.values()) marker.setMap(null);
    this.markers.clear();
    this.markerMeta.clear();
    this.infoWindow?.close();
    this.infoWindow = null;
    this.clearTrack();
    this.directionsRenderer?.setMap(null);
    this.directionsRenderer = null;
    this.map = null;
  }
}
